if (!customElements.get('pickup-availability')) {
  customElements.define(
    'pickup-availability',
    class PickupAvailability extends HTMLElement {
      requestId = 0;

      constructor() {
        super();

        this.errorHtml = this.querySelector('template').content.firstElementChild.cloneNode(true);
        this.onClickRefreshList = this.onClickRefreshList.bind(this);
        if (this.hasAttribute('available')) this.fetchAvailability(this.dataset.variantId);
      }

      disconnectedCallback() {
        this.requestId += 1;
        this.abortController?.abort();
        this.removeDrawer();
      }

      removeDrawer() {
        if (this.drawer?.hasAttribute('open')) this.drawer.hide();
        this.drawer?.remove();
        this.drawer = null;
      }

      fetchAvailability(variantId) {
        if (!variantId) return;
        this.dataset.variantId = String(variantId);
        const requestId = ++this.requestId;
        this.abortController?.abort();
        const controller = new AbortController();
        this.abortController = controller;
        this.removeDrawer();
        this.removeAttribute('available');
        this.innerHTML = '';

        let rootUrl = this.dataset.rootUrl;
        if (!rootUrl.endsWith('/')) {
          rootUrl = rootUrl + '/';
        }
        const variantSectionUrl = `${rootUrl}variants/${variantId}/?section_id=pickup-availability`;

        return fetch(variantSectionUrl, { signal: controller.signal })
          .then((response) => {
            if (!response.ok) throw new Error(`Pickup response ${response.status}`);
            return response.text();
          })
          .then((text) => {
            if (controller.signal.aborted || requestId !== this.requestId || !this.isConnected) return;
            const sectionInnerHTML = new DOMParser()
              .parseFromString(text, 'text/html')
              .querySelector('.shopify-section');
            if (!sectionInnerHTML) throw new Error('Missing pickup section');
            this.renderPreview(sectionInnerHTML);
          })
          .catch((e) => {
            if (controller.signal.aborted || requestId !== this.requestId || !this.isConnected) return;
            this.renderError();
          });
      }

      onClickRefreshList() {
        return this.fetchAvailability(this.dataset.variantId);
      }

      update(variant) {
        if (variant?.available) {
          return this.fetchAvailability(variant.id);
        } else {
          this.requestId += 1;
          this.abortController?.abort();
          this.dataset.variantId = variant?.id ? String(variant.id) : '';
          this.removeDrawer();
          this.removeAttribute('available');
          this.innerHTML = '';
        }
      }

      renderError() {
        this.innerHTML = '';
        this.appendChild(this.errorHtml.cloneNode(true));
        this.querySelector('pickup-availability-preview')?.setAttribute('role', 'status');

        this.querySelector('button').addEventListener('click', this.onClickRefreshList);
      }

      renderPreview(sectionInnerHTML) {
        this.removeDrawer();
        if (!sectionInnerHTML.querySelector('pickup-availability-preview')) {
          this.innerHTML = '';
          this.removeAttribute('available');
          return;
        }
        const drawer = sectionInnerHTML.querySelector('pickup-availability-drawer');
        if (!drawer) throw new Error('Missing pickup drawer');

        this.innerHTML = sectionInnerHTML.querySelector('pickup-availability-preview').outerHTML;
        this.setAttribute('available', '');

        this.drawer = drawer;
        document.body.appendChild(drawer);
        const colorClassesToApply = (this.dataset.productPageColorScheme || '').split(' ').filter(Boolean);
        colorClassesToApply.forEach((colorClass) => {
          drawer.classList.add(colorClass);
        });

        const button = this.querySelector('button');
        if (button)
          button.addEventListener('click', (evt) => {
            this.drawer?.show(evt.currentTarget);
          });
      }
    }
  );
}

if (!customElements.get('pickup-availability-drawer')) {
  customElements.define(
    'pickup-availability-drawer',
    class PickupAvailabilityDrawer extends HTMLElement {
      constructor() {
        super();

        this.onBodyClick = this.handleBodyClick.bind(this);

        this.querySelector('button').addEventListener('click', () => {
          this.hide();
        });

        this.addEventListener('keyup', (event) => {
          if (event.code.toUpperCase() === 'ESCAPE') this.hide();
        });
      }

      handleBodyClick(evt) {
        const target = evt.target;
        if (
          target != this &&
          !target.closest('pickup-availability-drawer') &&
          !this.focusElement?.contains(target)
        ) {
          this.hide();
        }
      }

      hide() {
        this.removeAttribute('open');
        document.body.removeEventListener('click', this.onBodyClick);
        document.body.classList.remove('overflow-hidden');
        removeTrapFocus(this.focusElement);
      }

      show(focusElement) {
        this.focusElement = focusElement;
        this.setAttribute('open', '');
        document.body.addEventListener('click', this.onBodyClick);
        document.body.classList.add('overflow-hidden');
        trapFocus(this);
      }
    }
  );
}
